<template>
  <div class="trainee-list-modal-overlay">
    <transition name="modal-expand">
      <div v-if="isVisible" class="trainee-list-modal" @click.stop>
        <div class="modal-content">
          <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
          <h1>Edit User ID: {{ newUser.user_id }}</h1>

          <div class="add">
            <form @submit.prevent="updateUser">
              <div class="form-group">
                <label for="name">First Name:</label>
                <input type="text" class="form-control rounded" id="first_name" name="first_name"
                  placeholder="Enter First Name" v-model="newUser.first_name" />
              </div>
              <div class="form-group">
                <label for="name">Last Name:</label>
                <input type="text" class="form-control rounded" id="last_name" name="last_name"
                  placeholder="Enter Last Name" v-model="newUser.last_name" />
              </div>
              <div class="form-group">
                <label for="email">Email:</label>
                <input type="text" class="form-control rounded" id="email" name="email"
                  placeholder="Enter Email Address" v-model="newUser.email" />
              </div>
              <div class="form-group">
                <label for="license_no">License No:</label>
                <input type="text" class="form-control rounded" id="license_no" name="license_no"
                  title="License No may only contain letters and numbers"
                  placeholder="Professional ID" v-model="newUser.license_no" />
              </div>
              <div class="form-group">
                <label for="date_of_birth">Date of Birth:</label>
                <input type="text" class="form-control rounded" id="date_of_birth" name="date_of_birth"
                  placeholder="DD/MM/YYYY" v-model="newUser.date_of_birth" />
              </div>
              <div class="form-group">
                <label for="place_of_birth">Place of Birth:</label>
                <input type="text" class="form-control rounded" id="place_of_birth" name="place_of_birth"
                  placeholder="Enter the Place of Birth" v-model="newUser.place_of_birth" />
              </div>
              <div v-if="!reissuedPassword">
                <div class="form-group">
                  <label for="password">Password:</label>
                  <input :type="showPassword ? 'text' : 'password'" class="form-control rounded" id="password"
                    name="password" placeholder="Create Password" v-model="newUser.password" />
                  <font-awesome-icon class="eye-icon" :icon="['far', showPassword ? 'eye' : 'eye-slash']"
                    @click="toggleShowPassword" />
                </div>
                <div v-if="passwordRules && !passwordValid" class="password-rules" style="margin-top: -20px; margin-left: 50px;">
                  <small>{{ passwordRules.description }}</small>
                </div>
                <div class="form-group">
                  <label for="reenteredPassword">Re-enter Password:</label>
                  <input :type="showPassword ? 'text' : 'password'" class="form-control rounded" id="reenteredPassword"
                    name="reenteredPassword" placeholder="Re-enter Password" v-model="newUser.reenteredPassword" />
                </div>
              </div>
              <div v-if="!reissuedPassword">
                <button
                  type="button" class="reissue-button" @click="reissueTempPassword" :disabled="reissueLoading || !newUser.user_id">
                  {{ reissueLoading ? 'Re-issuing...' : 'Re-issue Temporary Password' }}
                </button>
              </div>
                <div v-if="reissuedPassword" class="alert alert-info" style="white-space: pre-wrap;">
                  <b>Temporary password (shown once):</b> {{ reissuedPassword }}
                </div>
              <div v-if="!reissuedPassword">
                <p>
                  <b>Note: </b>Leave the password field empty if you do not wish to update it
                </p>
              </div>
              <div v-if="showAlert" class="alert alert-danger error-message" role="alert">
                <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
              </div>
              <div>
                <button class="btn btn-primary rounded" type="submit">Update User</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>

import axios from 'axios';

export default {
  props: {
    users: Array,
    user: { type: Object, default: null }, // null = Add mode
  },
  data() {
    return {
      newUser: {
        first_name: '',
        last_name: '',
        password: '',
        reenteredPassword: '',
        email: '',
        license_no: '',
        role: '',
        second_role: '',
        category: '',
        department: '',
        date_of_birth: '',
        place_of_birth: '',
      },
      showAlert: false,
      currentTime: '',
      errorMessages: {},
      errorMessage: '',
      loggedInUser: '',
      showUserList: false,
      showPassword: false,
      isVisible: false,
      reissueLoading: false,
      reissuedPassword: '',
      passwordRules: null,
    };
  },
  computed: {
    filteredSecondRoleOptions() {
      // Define all possible roles
      const roles = ['practical_manager', 'project_manager', 'instructor', 'trainee'];
      // Exclude the selected `newUser.role`
      return roles.filter(role => role !== this.newUser.role);
    },
    passwordChecklist() {
      const pw = this.newUser.password || '';
      const min = this.passwordRules?.min ?? 8;

      return {
        min: pw.length >= min,
        letter: /[A-Za-z]/.test(pw),
        number: /\d/.test(pw),
      };
    },
    passwordValid() {
      return (
        this.passwordChecklist.min &&
        this.passwordChecklist.letter &&
        this.passwordChecklist.number
      );
    },
  },
  methods: {
    async reissueTempPassword() {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const ok = window.confirm(
        `Re-issue a new temporary password for ${this.newUser.email}?\n\nThis will invalidate their current password and email a new one.`
      );
      if (!ok) return;

      try {
        this.reissueLoading = true;
        this.reissuedPassword = '';

        const res = await axios.post(
          `/api/users/${this.newUser.user_id}/reissue-temp-password`,
          {},
          { headers }
        );

        if (res.status === 200 && res.data?.success) {
          this.reissuedPassword = res.data.data.temporary_password;
          // clear manual fields to avoid confusion
          this.newUser.password = '';
          this.newUser.reenteredPassword = '';
          window.alert('Temporary password re-issued and emailed to the trainee.');
        } else {
          window.alert('Failed to re-issue password.');
        }
      } catch (e) {
        console.error(e);
        window.alert('Error re-issuing temporary password.');
      } finally {
        this.reissueLoading = false;
      }
    },
    async copyTempPassword() {
      try {
        await navigator.clipboard.writeText(this.reissuedPassword);
        window.alert('Copied to clipboard.');
      } catch {
        window.alert('Could not copy. Please copy manually.');
      }
    },
    toggleShowPassword() {
      this.showPassword = !this.showPassword;
    },
    buildRolesArray() {
      const roles = [this.newUser.role, this.newUser.second_role].filter(Boolean);
      return [...new Set(roles)]; // ensure no duplicates
    },
    formatRole(role) {
      // Format role names to be more readable (e.g., capitalize)
      return role.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    },
    formatDate(dateString) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month}-${day}`;
    },
    async updateUser() {
      // Check if password is being updated and validate passwords
      if (this.newUser.password || this.newUser.reenteredPassword) {
        if (this.newUser.password !== this.newUser.reenteredPassword) {
          this.errorMessage = 'Passwords do not match';
          this.showAlert = true;
          return;
        }
      }
      try {
        const user_id = this.newUser.user_id;
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Create a data object with the fields you want to update
        const data = {
          first_name: this.newUser.first_name,
          last_name: this.newUser.last_name,
          email: this.newUser.email,
          license_no: this.newUser.license_no,
          roles: this.buildRolesArray(),
          category: this.newUser.category,
          department: this.newUser.department,
          date_of_birth: this.formatDate(this.newUser.date_of_birth),
          place_of_birth: this.newUser.place_of_birth,

        };

        // Check if the password field is not empty before adding it to the data object
        if (this.newUser.password) {
          data.password = this.newUser.password;
          data.password_confirmation = this.newUser.reenteredPassword;
        }

        const response = await axios.put(`/api/users/${user_id}`, data, { headers });

        if (response.status === 200) {
          window.alert('User updated Successfully');
          this.newUser = {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            reenteredPassword: '',
            license_no: '',
            role: '',
            second_role: '',
            category: '',
            department: '',
            date_of_birth: '',
            place_of_birth: '',
          };
          this.errorMessage = '';
          this.showAlert = false;
        }
        window.location.reload();
      } catch (error) {
        console.error('Error adding User:', error);

        if (error.response && error.response.data && error.response.data.errors) {
          // Handle validation errors
          const validationErrors = error.response.data.errors;

          // Build an error message from the validation errors
          let errorMessage = 'Validation errors:';
          for (const field in validationErrors) {
            errorMessage += `\n${field}: ${validationErrors[field][0]}`;
          }

          this.errorMessage = errorMessage;
        } else if (
          error.response &&
          error.response.status === 500 &&
          error.response.data &&
          error.response.data.message &&
          error.response.data.message.includes('users_license_no_unique')
        ) {
          // Handle the specific duplicate entry error
          this.errorMessage = 'A User with this License No already exists';
        } else if (
          error.response &&
          error.response.status === 500 &&
          error.response.data &&
          error.response.data.message &&
          error.response.data.message.includes('users_email_unique')
        ) {
          // Handle the specific duplicate entry error
          this.errorMessage = 'A User with this Email already exists';
        } else {
          // Handle other errors
          this.errorMessage = 'An error occurred while updating the User.';
        }

        this.showAlert = true;
      }
    },
    close() {
      this.$emit('close'); // Emit an event to indicate the modal should be closed
    },
    async fetchPasswordRules() {
      try {
        const res = await axios.get('/api/auth/password-rules');
        this.passwordRules = res.data;
      } catch (e) {
        console.error('Failed to fetch password rules', e);
        this.passwordRules = { description: 'Minimum 8 characters, at least 1 letter and 1 number.', min: 8, requires: { letters: true, numbers: true } };
      }
    },
  },
  async mounted() {
    this.isVisible = true;
    if (this.user) {
      const existingRoles = Array.isArray(this.user.roles) ? this.user.roles : [];
      this.newUser = {
        ...this.newUser,
        ...this.user,
        role: existingRoles[0] || '',
        second_role: existingRoles[1] || '',
      };
    }
    await this.fetchPasswordRules();
  },
}
</script>

<style scoped>
.trainee-list-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  /* Semi-transparent background */
  display: flex;
  justify-content: center;
  z-index: 1000;
}

h2 {
  margin-top: 30px;
  font-size: 30px;
  margin-bottom: 30px;
}

/* Style your modal content */
.trainee-list-modal {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  margin-top: 20px;
  width: 80%;
  max-width: 800px;
  /* Limit the maximum width of the modal */
  padding: 20px;
  position: relative;
  max-height: 95%;
}

.modal-content {
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  padding: 15px;
}

.btn {
  border: 1px solid #102359;
  background: #2952c4;
  color: white;
  cursor: pointer;
  text-align: center;
  transition: transform 0.2s, box-shadow 0.2s;
}

p {
  padding: 20px;
  color: #be0808;
  border: 1px solid rgb(204, 202, 202);
  width: 70%;
  margin-left: 15%;
  border-radius: 5px;
  font-family: math;
}

.btn:hover {
  background-color: #0c1a61;
  transition: transform 0.2s, box-shadow 0.2s;
}


.form-control.rounded {
  height: 40px;
}

.form-group {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.form-group label {
  width: 150px;
  margin-right: 30px;
  text-align: right;
  margin-bottom: 0px;
}

.form-group input.form-control,
.form-group select.form-control {
  flex: 1;
  width: auto;
  margin-left: 0;
  margin-bottom: 0px;
}

.form-control {
  border-radius: 5px;
}

#date_of_birth,
#end_date {
  flex: 0 0 180px;
  /* don't grow, don't shrink, fixed 100px */
  width: 180px;
}

h1 {
  padding-top: 10px;
  margin-bottom: 30px;
  margin-left: 30px;
  font-size: 2rem;
  text-align: left;
}

.eye-icon {
  position: absolute;
  right: 40px;
  cursor: pointer;
  color: grey;
}

.eye-icon:hover {
  color: rgb(77, 77, 77);
}

.reissue-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.reissued-password {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background-color: #e7f3ff;
  border: 1px solid #b6daff;
  border-radius: 4px;
  white-space: nowrap;
}

.reissue-button {
  border: 1px solid rgb(46, 73, 54);
  border-radius: 5px;
  background-color: #5d975e; /* Green */
  color: white;
  font-size: 14px;
  padding: 5px 10px;
  width: 220px;
  text-align: center;
  margin-bottom: 15px;
  margin-top: 0px;
}

.reissue-button:hover {
  background-color: #3e743f;
}

.copy-button {
  background-color: #6c757d;
  border: 1px solid #4d5358;
  color:white;
  padding: 5px 20px;
  font-size: 14px;
  padding: 5px 5px;
  width: 120px;
  border-radius: 5px;
}

.copy-button:hover {
  background-color: #545a5f;
}
</style>